'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { CustomImage as Image } from '..';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import gsap from 'gsap';
import {
  defaultTechnicalSpecsConfig,
  resolveAccordionSections,
  type TechnicalSpecsConfig,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type TechnicalSpecsImageInfo,
} from './technicalSpecsConfigs';
import styles from './TechnicalSpecs.module.css';

// Internal: same shape as TechnicalSpecsImageInfo for TwoImageSection
interface ImageInfo {
  src: string;
  alt: string;
  textBold: string;
  textNormal: string;
}

interface AccordionItem {
  title: React.ReactNode;
  content: React.ReactNode;
}

interface TechnicalSpecsProps {
  className?: string;
  htmlId?: string;
  /** Data-driven config. When omitted, Ring AIR (default) is shown. Pass getRingProTechnicalSpecsConfig(t) for Ring PRO. */
  config?: TechnicalSpecsConfig;
  /** Legacy: direct overrides when config is not used (e.g. Rare) */
  title?: React.ReactNode;
  images?: ImageInfo[];
  accordionItems?: AccordionItem[];
  imageBackgroundColor?: string;
  /** When true, renders a flat list on mobile instead of an accordion. */
  disableMobileAccordion?: boolean;
}

const TwoImageSection = ({
  className,
  images,
  imageBackgroundColor,
}: {
  className?: string;
  images?: ImageInfo[];
  imageBackgroundColor?: string;
}) => {
  const t = useTranslations('ring');
  const container = useRef<HTMLDivElement | null>(null);
  const image1 = useRef<HTMLImageElement | null>(null);
  const image2 = useRef<HTMLImageElement | null>(null);

  const gsapContext = useGsapContext();

  // Default images for backward compatibility
  const defaultImages: ImageInfo[] = [
    {
      src: 'web_v2/illustrations/air-front-tilt.png/w_1920',
      alt: 'UH Ring AIR',
      textBold: 'ring.techSpec.twoImgInfo.lightA',
      textNormal: 'ring.techSpec.twoImgInfo.lightB',
    },
    {
      src: 'web_v2/illustrations/air-full-tilt.png/w_1920',
      alt: 'UH Ring AIR',
      textBold: 'ring.techSpec.twoImgInfo.sleekA',
      textNormal: 'ring.techSpec.twoImgInfo.sleekB',
    },
  ];

  const displayImages = images || defaultImages;

  const animInit = () => {
    if (!window || !image1.current || !image2.current) return;

    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 1,
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0,
        },
      });

      tl.fromTo(
        image1.current,
        {
          y: 200,
          scale: 1,
        },
        {
          y: -100,
          scale: 1.2,
        },
      );

      tl.fromTo(
        image2.current,
        {
          y: 200,
          scale: 2.5,
        },
        {
          y: -100,
          scale: 1.5,
        },
        '-=1',
      );
    });
  };

  useIsomorphicLayoutEffect(() => {
    const timer = setTimeout(() => {
      animInit();
    }, 100);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <section
      className={`${styles.twoImageSection}${className ? ` ${className}` : ''}`}
      ref={container}
    >
      <div className="content">
        <div className="img-info-element">
          <div
            className="img-container"
            style={{ background: imageBackgroundColor || '#000' }}
          >
            <Image
              ref={image1}
              src={displayImages[0]?.src}
              fill
              alt={displayImages[0]?.alt}
            />
          </div>
          <p>
            <b>
              {displayImages[0]?.textBold?.includes('ring.techSpec.')
                ? t(displayImages[0].textBold)
                : displayImages[0]?.textBold}
            </b>{' '}
            {displayImages[0]?.textNormal?.includes('ring.techSpec.')
              ? t(displayImages[0].textNormal)
              : displayImages[0]?.textNormal}
          </p>
        </div>
        {displayImages[1] && (
          <div className="img-info-element">
            <div
              className="img-container"
              style={{ background: imageBackgroundColor || '#000' }}
            >
              <Image
                ref={image2}
                src={displayImages[1].src}
                fill
                alt={displayImages[1].alt}
              />
            </div>
            <p>
              <b>
                {displayImages[1].textBold?.includes('ring.techSpec.')
                  ? t(displayImages[1].textBold)
                  : displayImages[1].textBold}
              </b>{' '}
              {displayImages[1].textNormal?.includes('ring.techSpec.')
                ? t(displayImages[1].textNormal)
                : displayImages[1].textNormal}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export const TechnicalSpecs = ({
  className,
  htmlId,
  config: configProp,
  title: titleProp,
  images: imagesProp,
  accordionItems: accordionItemsProp,
  imageBackgroundColor: imageBackgroundColorProp,
  disableMobileAccordion,
}: TechnicalSpecsProps) => {
  const t = useTranslations('ring');
  const rootRef = useRef<HTMLDivElement>(null);

  // Use config (data-driven), or legacy props, or default Ring AIR config
  const defaultImages =
    defaultTechnicalSpecsConfig.heroSection.type === 'images'
      ? defaultTechnicalSpecsConfig.heroSection.images
      : [];
  const config: TechnicalSpecsConfig =
    configProp ??
    (accordionItemsProp || imagesProp
      ? {
          heroSection: {
            type: 'images',
            images: imagesProp ?? defaultImages,
          },
          accordionItems: defaultTechnicalSpecsConfig.accordionItems,
        }
      : defaultTechnicalSpecsConfig);

  const useLegacyAccordion = !configProp && !!accordionItemsProp;
  const resolvedAccordionItems = useLegacyAccordion
    ? accordionItemsProp
    : resolveAccordionSections(config.accordionItems, t);
  const title = titleProp ?? config.title ?? t('ring.TECH_SPECIFICATIONS');
  const imageBackgroundColor =
    imageBackgroundColorProp ?? config.imageBackgroundColor;

  return (
    <section
      className={`${styles.technicalSpecs}${className ? ` ${className}` : ''}`}
      id={htmlId}
      ref={rootRef}
    >
      <h3>{title}</h3>
      {config.heroSection.type === 'custom' ? (
        config.heroSection.content
      ) : (
        <TwoImageSection
          images={config.heroSection.images as ImageInfo[]}
          imageBackgroundColor={imageBackgroundColor}
        />
      )}
      <TechnicalSpec
        accordionItems={resolvedAccordionItems}
        disableMobileAccordion={disableMobileAccordion}
      />
    </section>
  );
};

const TechnicalSpec = ({
  className,
  accordionItems: customAccordionItems,
  disableMobileAccordion,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
  disableMobileAccordion?: boolean;
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 768) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const mobileContent = disableMobileAccordion ? (
    <MobileListView accordionItems={customAccordionItems} />
  ) : (
    <MobileAccordion accordionItems={customAccordionItems} />
  );

  return (
    <div
      className={`${styles.technicalSpec} u-grid${className ? ` ${className}` : ''}`}
    >
      <div>
        {!isMobile ? (
          <DesktopTable accordionItems={customAccordionItems} />
        ) : (
          mobileContent
        )}
      </div>
    </div>
  );
};

export const DesktopTable = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
  const displayItems = customAccordionItems ?? [];

  return (
    <table
      className={`${styles.desktopTable}${className ? ` ${className}` : ''}`}
    >
      <tbody>
        {displayItems.map((item, index) => (
          <tr key={index}>
            <td>
              {/* <span className="bold">{String(index + 1).padStart(2, '0')}</span> */}
            </td>
            <td>
              <span className="bold">{item.title}</span>
            </td>
            <td>
              <div className="content">{item.content}</div>
            </td>
            <td>
              <div></div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

type AccordionProps = {
  className?: string;
  data: Array<{
    title: React.ReactNode;
    content: React.ReactNode;
  }>;
};

const Accordion = ({ className, data }: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState(0);

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (event.keyCode === 13) setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <div
      className={`${styles.accordionRoot} accordion${className ? ` ${className}` : ''}`}
    >
      {data.map((data, index) => {
        const { title, content } = data;
        return (
          <div
            className={`accordion-item ${openIndex === index ? 'active' : ''} `}
            key={index}
          >
            <div
              className="accordion-header"
              onClick={() => handleClick(index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              role="presentation"
              tabIndex={0}
            >
              <>
                {title}
                <span className="icon">{openIndex === index ? '-' : '+'}</span>
              </>
            </div>
            <div className="accordion-body">{content}</div>
          </div>
        );
      })}
    </div>
  );
};

const MobileListView = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
  const displayItems = customAccordionItems ?? [];
  return (
    <div
      className={`${styles.mobileListView}${className ? ` ${className}` : ''}`}
    >
      {displayItems.map((item, index) => (
        <div className="list-item" key={index}>
          <div className="list-item__title">{item.title}</div>
          <div className="list-item__content">{item.content}</div>
        </div>
      ))}
    </div>
  );
};

export const MobileAccordion = ({
  className,
  accordionItems: customAccordionItems,
}: {
  className?: string;
  accordionItems?: AccordionItem[];
}) => {
  const displayItems = customAccordionItems ?? [];

  return (
    <div
      className={`${styles.mobileAccordion}${className ? ` ${className}` : ''}`}
    >
      <Accordion data={displayItems} />
    </div>
  );
};
